import type { ReactNode } from 'react'
import { CalendarClock, Cake } from 'lucide-react'
import { Badge, cx } from '../ui/kit'
import type { BirthdayInfo, GuestStats } from '../lib/guests'

/**
 * Die Kennzahlen eines Gastes als Kacheln.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIESE ZAHLEN UND NICHT DIE ANDEREN.
 *
 * Ein Vermieter öffnet die Kartei mit einer Frage im Kopf, und es ist fast immer
 * dieselbe: „Wer ist das, und was ist er mir wert?" Darauf antworten zwei Zahlen —
 * wie oft er da war und was er gebracht hat. Alles andere ist Beiwerk, das man liest,
 * WENN man schon liest.
 *
 * Die Rangfolge ist deshalb nicht Geschmack, sondern Häufigkeit:
 *
 *   1. AUFENTHALTE  — „war schon 4× da" ist der Satz, wegen dem die Kartei existiert.
 *   2. GEBUCHT      — die zweite Hälfte derselben Frage. Bewusst der gebuchte Wert
 *                    und nicht der abgerechnete Umsatz: viele kleine Vermieter
 *                    schreiben nicht für jeden Aufenthalt eine Rechnung, und eine
 *                    Kachel, die bei ihnen dauerhaft „€ 0,00" zeigt, beantwortet die
 *                    Frage nicht, sondern lässt die Kartei kaputt aussehen. Was
 *                    davon abgerechnet ist, steht als Hinweis darunter — beides
 *                    sichtbar, beides beschriftet, keins als das andere getarnt.
 *   3. Ø JE AUFENTHALT — trennt den Gast, der viermal eine Nacht bleibt, von dem, der
 *                    viermal eine Woche bleibt. Bei gleichem Umsatz sind das zwei
 *                    völlig verschiedene Gäste, und die Summe allein verrät es nicht.
 *   4. Ø DAUER      — entscheidet, ob eine Anfrage über zwei Nächte zu diesem Gast
 *                    passt oder ob man ihn auf eine Woche ansprechen sollte.
 *   5. ZULETZT      — „ist der noch aktuell?"
 *   6. LIEBSTE EINHEIT — die Zahl, mit der man einen Stammgast beeindruckt, ohne dass
 *                    er merkt, dass es in einer Datenbank steht.
 *
 * WAS BEWUSST KEINE KACHEL BEKOMMT: offene Beträge, Stornos, No-Shows und die
 * Vorlaufzeit. Die ersten drei sind AUSNAHMEN. Eine Kachel „0 Stornos" verbraucht
 * denselben Platz wie eine Kachel „4 Aufenthalte" und sagt nichts — sie erzieht nur
 * dazu, die Zeile zu überfliegen, in der eines Tages „2 Stornos" steht. Deshalb
 * stehen sie in einer Nebenzeile, die es nur gibt, wenn es sie gibt. Die Vorlaufzeit
 * ist kein Ausnahmefall, aber auch keine Antwort auf die Frage von oben: sie ist
 * Planungswissen und steht deshalb ebenfalls unten, ruhig statt warnend.
 *
 * MOBIL ZUERST: zwei Spalten auf dem Telefon, drei ab `sm`. Sechs Kacheln sind auf
 * dem Handy drei Daumenbreiten hoch — mehr wäre eine Tabelle, und eine Tabelle liest
 * niemand im Vorbeigehen.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export default function GuestStatsGrid({
  stats, de, formatMoney, formatDate, unitName, birthday,
}: {
  stats: GuestStats
  de: boolean
  formatMoney: (v: number) => string
  formatDate: (iso: string) => string
  /** Name der Einheit; leer, wenn sie nicht (mehr) existiert. */
  unitName: (id: string) => string
  /** Alter und nächster Geburtstag, wenn ein Geburtsdatum erfasst ist. */
  birthday: BirthdayInfo | null
}) {
  // Ein Gast ohne angetretenen Aufenthalt: Durchschnitte über null Aufenthalte sind
  // keine Null, sondern eine Aussage, die es nicht gibt. Statt vier Kacheln mit „0 €"
  // steht hier der eine Satz, der tatsächlich zutrifft.
  if (stats.stays === 0) {
    return (
      <div className="space-y-2">
        <div className="rounded-xl bg-surface-2 px-3 py-2.5 text-sm">
          {/*
            Der gebuchte Wert gehört in diesen Satz: „noch nie da, aber eine Woche
            für 1.240 € gebucht" ist ein anderer Gast als „noch nie da, zwei Nächte".
            Beide haben null Aufenthalte, und die Kacheln oben können sie deshalb
            nicht unterscheiden.
          */}
          {stats.upcoming > 0
            ? (de
              ? `Noch nie da — ${stats.upcoming} Aufenthalt${stats.upcoming > 1 ? 'e' : ''} gebucht, ${formatMoney(stats.booked)}.`
              : `Never stayed yet — ${stats.upcoming} booked, ${formatMoney(stats.booked)}.`)
            : (de ? 'Noch kein Aufenthalt.' : 'No stay yet.')}
        </div>
        <SideLine stats={stats} de={de} formatMoney={formatMoney} birthday={birthday} />
      </div>
    )
  }

  const favourite = unitName(stats.favouriteUnitId ?? '')
  // Erst ab zwei Aufenthalten in derselben Einheit ist es eine Vorliebe. Bei einem
  // einzigen Besuch wäre die Kachel „Liebste Einheit" die Umbenennung einer
  // Selbstverständlichkeit.
  const showFavourite = stats.favouriteUnitStays >= 2 && favourite !== ''

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Metric
          label={de ? 'Aufenthalte' : 'Stays'}
          value={String(stats.stays)}
          hint={stats.upcoming > 0
            ? (de ? `+${stats.upcoming} gebucht` : `+${stats.upcoming} booked`)
            : undefined}
          strong
        />
        {/*
          GEBUCHT, NICHT „UMSATZ". Die grosse Zahl kommt aus den Buchungen und nicht
          aus den Rechnungen — siehe `GuestStats.booked`. Der Hinweis darunter sagt,
          was davon abgerechnet ist, und zwar IMMER, auch wenn es null ist: „gebucht
          € 1.240 / abgerechnet € 0" ist eine Auskunft über die eigene Belegführung,
          eine kommentarlose Null wäre nur ein Verdacht auf einen Fehler.
        */}
        <Metric
          label={de ? 'Gebucht' : 'Booked'}
          value={formatMoney(stats.booked)}
          hint={de
            ? `${formatMoney(stats.revenue)} abgerechnet`
            : `${formatMoney(stats.revenue)} invoiced`}
          strong
        />
        <Metric
          label={de ? 'Ø je Aufenthalt' : 'Ø per stay'}
          value={formatMoney(stats.bookedPerStay)}
        />
        <Metric
          label={de ? 'Ø Dauer' : 'Ø length'}
          value={de
            ? `${format1(stats.nightsPerStay)} N.`
            : `${format1(stats.nightsPerStay)} n.`}
          hint={de ? `${stats.nights} Nächte gesamt` : `${stats.nights} nights total`}
        />
        <Metric
          label={de ? 'Zuletzt' : 'Last stay'}
          value={stats.lastStay ? formatDate(stats.lastStay) : '—'}
          hint={stats.firstStay
            ? (de ? `seit ${formatDate(stats.firstStay)}` : `since ${formatDate(stats.firstStay)}`)
            : undefined}
        />
        {showFavourite && (
          <Metric
            label={de ? 'Liebste Einheit' : 'Favourite unit'}
            value={favourite}
            hint={de
              ? `${stats.favouriteUnitStays}× von ${stats.stays}`
              : `${stats.favouriteUnitStays} of ${stats.stays}`}
          />
        )}
      </div>

      <SideLine stats={stats} de={de} formatMoney={formatMoney} birthday={birthday} />
    </div>
  )
}

/**
 * Die Nebenzeile: was nur dann dasteht, wenn es etwas bedeutet.
 *
 * Zwei Tonlagen im selben Band. Warnend, was Arbeit macht oder Geld kostet (offen,
 * nicht erschienen, storniert), ruhig, was man einfach wissen sollte (Vorlaufzeit,
 * Geburtstag). Der Unterschied muss sichtbar sein, sonst ist die Zeile wieder nur
 * eine Zahlenkolonne mit runden Ecken.
 */
function SideLine({
  stats, de, formatMoney, birthday,
}: {
  stats: GuestStats
  de: boolean
  formatMoney: (v: number) => string
  birthday: BirthdayInfo | null
}) {
  const bits: ReactNode[] = []

  if (stats.open > 0) {
    bits.push(
      <Badge key="open" tone="warn">
        {de ? `${formatMoney(stats.open)} offen` : `${formatMoney(stats.open)} outstanding`}
      </Badge>,
    )
  }
  // No-Shows vor Stornos: ein Gast, der nicht abgesagt hat, ist der teurere Fall.
  if (stats.noShows > 0) {
    bits.push(
      <Badge key="noshow" tone="danger">
        {de ? `${stats.noShows}× nicht erschienen` : `${stats.noShows}× no-show`}
      </Badge>,
    )
  }
  if (stats.cancellations > 0) {
    bits.push(
      <Badge key="cancel" tone="neutral">
        {de ? `${stats.cancellations}× storniert` : `${stats.cancellations}× cancelled`}
      </Badge>,
    )
  }
  if (stats.leadTimeDays !== null && stats.stays + stats.upcoming >= 2) {
    bits.push(
      <Badge key="lead" tone="neutral">
        <CalendarClock size={12} />
        {de
          ? `bucht ${stats.leadTimeDays} Tage im Voraus`
          : `books ${stats.leadTimeDays} days ahead`}
      </Badge>,
    )
  }
  if (birthday) {
    bits.push(
      <Badge key="bday" tone={birthday.inDays <= 14 ? 'accent' : 'neutral'}>
        <Cake size={12} />
        {de
          ? `${birthday.age} Jahre · ${birthdayWhen(birthday, true)}`
          : `${birthday.age} years · ${birthdayWhen(birthday, false)}`}
      </Badge>,
    )
  }

  if (bits.length === 0) return null
  return <div className="flex flex-wrap gap-1.5">{bits}</div>
}

/** „heute", „morgen", „in 9 Tagen" — die Form, in der ein Mensch danach fragt. */
export function birthdayWhen(info: BirthdayInfo, de: boolean): string {
  if (info.inDays === 0) return de ? 'heute Geburtstag' : 'birthday today'
  if (info.inDays === 1) return de ? 'morgen Geburtstag' : 'birthday tomorrow'
  return de ? `Geburtstag in ${info.inDays} Tagen` : `birthday in ${info.inDays} days`
}

/** Eine Nachkommastelle, aber „7" statt „7,0" — die Null trägt hier keine Aussage. */
function format1(v: number): string {
  const rounded = Math.round(v * 10) / 10
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1).replace('.', ',')
}

function Metric({
  label, value, hint, strong,
}: { label: string; value: string; hint?: string; strong?: boolean }) {
  return (
    <div className={cx(
      'rounded-xl px-3 py-2 min-w-0',
      // Die beiden Zahlen, wegen denen jemand die Kartei öffnet, tragen den Akzent.
      // Nicht als Farbspiel: der Blick soll auf ihnen landen, bevor er wandert.
      strong ? 'bg-accent-soft' : 'bg-surface-2',
    )}>
      <div className={cx('text-[11px]', strong ? 'text-accent' : 'text-muted')}>{label}</div>
      <div className={cx(
        'font-semibold tabular-nums truncate',
        strong && 'text-lg leading-tight',
      )}>
        {value}
      </div>
      {hint && <div className="text-[11px] text-muted truncate">{hint}</div>}
    </div>
  )
}
